// src/utils/cache/index.js
// Main entry point for all caching utilities
// Single source of truth for all cache-related functionality

// Data/API caching (sessionStorage)
import { fetchWithCache } from './data-cache.js';
export {
    fetchWithCache,
    clearCache,
    clearAllCache,
    clearAreaCache,
    getImagesVersion
} from './data-cache.js';

import {
    preloadMinimalForArea,
    getCachedMinimalResults,
    getMinimalResultsForSpots
} from './minimal-spots-cache.js';

// Explore tags caching (in-memory)
export {
    batchLoadTagsForPacks,
    getTagsForPack,
    preloadTagsForPacks,
    clearExploreTagsCache
} from './explore-tags-cache.js';

// Minimal results caching (in-memory)
export {
    preloadMinimalForArea,
    getMinimalResultsForPack,
    getMinimalResultsForSpots,
    getCachedMinimalResults,
    clearMinimalSpotsCache,
    populateMinimalResults
} from './minimal-spots-cache.js';

// Area data preloading
export {
    preloadAreaData,
    preloadAreasList
} from './area-data-preloader.js';

// Static assets preloading
export {
    preloadLoadingLogo,
    preloadLogos,
    preloadLanguageFlags,
    preloadIconSprites,
    preloadAllStaticAssets,
    preloadCustomAssets,
    isAssetPreloaded,
    clearStaticAssetsCache
} from './static-assets-cache.js';

// Image preloading cache (browser HTTP cache)
export {
    preloadImages,
    preloadMarkerPopupImages,
    preloadAllCarouselImages,
    preloadAllLightboxImages,
    preloadFavicon,
    clearImagePreloadCache,
    isImagePreloaded
} from './image-cache.js';

// Cards batch loading and caching (in-memory)
export {
    batchLoadCardsForSpots,
    getCachedCards,
    getCardsForSpot,
    preloadCardsForSpots,
    clearCardsCache,
    clearAllCardsCache,
    hasCachedCards,
    getCardsForExplore,
    triggerAreaBatch,
    preloadAllCardsForArea,
    clearExploreCardsCache,
    hasCachedExploreCards,
    getCardsForMap,
    preloadCardsForMap,
    clearMapCardsCache,
    hasCachedMapCards
} from './cards-cache.js';

// Spots batch loading and caching (in-memory)
export {
    batchLoadSpots,
    getCachedSpots,
    getSpot,
    preloadSpots,
    clearSpotsCache,
    hasCachedSpot
} from './spots-cache.js';

// --- Compatibility helpers (used by older modules like header-explore.js) ---

/**
 * Preload minimal spots for an entire area.
 * Returns the raw minimal data (id, lat, lng, icon, color, tags).
 */
export async function preloadMinimalSpotsForArea(areaId, maps = null) {
    if (!areaId) return null;

    // Use the unified area preloader
    // It handles the efficient area-wide fetch (get-spots-for-area.php)
    // which covers both maps (Discover) and packs (Explore)
    try {
        const areaMap = await preloadMinimalForArea(areaId);

        // Return format expected by some callers: { ids, minimal }
        const minimal = Array.from(areaMap.values());
        return {
            ids: minimal.map(s => s.id),
            minimal
        };
    } catch (e) {
        console.warn('[Cache] Failed to preload area minimal:', e);
        return null;
    }
}

/**
 * Returns cached minimal spots for an area in a `{ ids, minimal }` shape
 * adapting the maps cache (which stores a Map of spotId -> minimal).
 */
export function getCachedMinimalSpotsForArea(areaId) {
    const areaMap = getCachedMinimalResults(areaId);
    if (!areaMap) return null;
    const minimal = Array.from(areaMap.values());
    return { ids: minimal.map(s => s.id), minimal };
}

/**
 * Filter a cached minimal object by tag IDs. Returns same `{ ids, minimal }` shape.
 */
export function filterCachedMinimalSpots(cachedObj, tagIds = []) {
    if (!cachedObj || !Array.isArray(cachedObj.minimal)) return { ids: [], minimal: [] };
    if (!tagIds || tagIds.length === 0) return { ids: cachedObj.ids || [], minimal: cachedObj.minimal };
    const filtered = cachedObj.minimal.filter(spot => Array.isArray(spot.tags) && tagIds.some(id => spot.tags.includes(id)));
    return { ids: filtered.map(s => s.id), minimal: filtered };
}

/**
 * Convert minimal spot entries into the simplified spot objects expected by
 * `displaySpots` / map code. Accepts either an object `{ minimal: [] }` or
 * a direct array of minimal entries.
 */
export function convertMinimalToSpots(minimalSource, filteredIds = null) {
    let list = [];
    if (!minimalSource) return [];
    if (Array.isArray(minimalSource)) {
        list = minimalSource;
    } else if (Array.isArray(minimalSource.minimal)) {
        list = minimalSource.minimal;
    }

    if (filteredIds && Array.isArray(filteredIds)) {
        list = list.filter(s => filteredIds.includes(s.id));
    }

    return list.map(s => ({
        id: s.id,
        latitude: s.latitude !== undefined ? parseFloat(s.latitude) : s.latitude,
        longitude: s.longitude !== undefined ? parseFloat(s.longitude) : s.longitude,
        icon: s.icon,
        color: s.color,
        coordinates: s.coordinates || (s.latitude !== undefined && s.longitude !== undefined ? { latitude: parseFloat(s.latitude), longitude: parseFloat(s.longitude) } : undefined)
    }));
}